import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { cn } from "../../utils/tailwindUtils";
import { ArrowLeft, Search, Loader2, RefreshCw, AlertTriangle} from "lucide-react";
import { Select } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import { WA_CAMPAIGNS_URL, WA_CAMPAIGN_RECIPIENTS_URL } from "../../constants/api";

const STATUS_COLORS = {
    queued:    'bg-gray-100 text-gray-600',
    sent:      'bg-blue-100 text-blue-700',
    delivered: 'bg-teal-100 text-teal-700',
    read:      'bg-green-100 text-green-700',
    failed:    'bg-red-100 text-red-700',
    skipped:   'bg-yellow-100 text-yellow-700',
};

const KPI = ({ label, value, sub, color }) => (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
        <p className={cn("text-2xl font-bold mt-0.5", color || "text-gray-800 dark:text-gray-100")}>{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
);

export default function WhatsAppCampaignReport() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { fetch } = useApiMethods();

    const [campaign, setCampaign] = useState(null);
    const [recipients, setRecipients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');

    const load = async () => {
        setLoading(true);
        const [cRes, rRes] = await Promise.all([
            fetch(WA_CAMPAIGNS_URL),
            fetch(WA_CAMPAIGN_RECIPIENTS_URL(id)),
        ]);
        if (cRes?.status === 'success') {
            setCampaign((cRes.data || []).find(c => String(c.id) === String(id)) || null);
        }
        if (rRes?.status === 'success') setRecipients(rRes.data || []);
        setLoading(false);
    };

    useEffect(() => { load(); }, [id]);

    const filtered = recipients.filter(r => {
        if (statusFilter && r.status !== statusFilter) return false;
        if (search && !r.customer_name.toLowerCase().includes(search.toLowerCase()) &&
            !r.phone_number.includes(search)) return false;
        return true;
    });

    const pct = (n) => campaign?.sent_count > 0
        ? ` (${Math.round(n / campaign.sent_count * 100)}%)`
        : '';

    if (loading && !campaign) {
        return <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
    }

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center gap-3">
                <Button variant="outline" onClick={() => navigate('/whatsapp/campaigns')} className="text-sm flex items-center gap-2">
                    <ArrowLeft className="h-4 w-4" /> Back
                </Button>
                <div>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">{campaign?.name || 'Campaign Report'}</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{campaign?.template_name}</p>
                </div>
                <Button variant="outline" onClick={load} disabled={loading} className="ml-auto text-sm flex items-center gap-2">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    Refresh
                </Button>
            </div>

            {/* Why nothing was sent, stated at the top. A campaign that reached
                nobody used to be indistinguishable from one that had not run:
                zeros across the KPI row and "No recipients found." below, with
                the actual reason recorded nowhere. */}
            {campaign?.last_error && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/10
                                px-4 py-3 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                    <div className="text-sm text-amber-800 dark:text-amber-300">
                        <span className="font-medium">This run sent nothing. </span>
                        {campaign.last_error}
                    </div>
                </div>
            )}

            {/* KPI row */}
            {campaign && (
                <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3">
                    <KPI label="Targeted"   value={campaign.total_recipients} />
                    <KPI label="Sent"       value={campaign.sent_count}       color="text-blue-600" />
                    <KPI label="Delivered"  value={campaign.delivered_count}  sub={pct(campaign.delivered_count)}  color="text-teal-600" />
                    <KPI label="Read"       value={campaign.read_count}       sub={pct(campaign.read_count)}       color="text-green-600" />
                    <KPI label="Failed"     value={campaign.failed_count}     color="text-red-500" />
                    <KPI label="Opt-outs"   value={campaign.opt_out_count}    color="text-orange-500" />
                    <KPI label="Replies"    value={campaign.reply_count}      color="text-purple-600" />
                </div>
            )}

            {/* Recipients table */}
            <ContainerCard>
                <div className="flex items-center gap-3 mb-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                            className="pl-9"
                            placeholder="Search by name or phone..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <Select
                        className="w-36"
                        placeholder="All statuses"
                        allowClear
                        value={statusFilter || undefined}
                        options={[
                            { value: 'queued', label: 'Queued' },
                            { value: 'sent', label: 'Sent' },
                            { value: 'delivered', label: 'Delivered' },
                            { value: 'read', label: 'Read' },
                            { value: 'failed', label: 'Failed' },
                            { value: 'skipped', label: 'Skipped' },
                        ]}
                        onChange={v => setStatusFilter(v || '')}
                    />
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                                <th className="pb-2 font-medium">Contact</th>
                                <th className="pb-2 font-medium">Phone</th>
                                <th className="pb-2 font-medium">Status</th>
                                <th className="pb-2 font-medium">Sent At</th>
                                <th className="pb-2 font-medium">Delivered</th>
                                <th className="pb-2 font-medium">Read</th>
                                <th className="pb-2 font-medium">Failure</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(r => (
                                <tr key={r.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                    <td className="py-3 font-medium text-gray-800 dark:text-gray-100">{r.customer_name || '—'}</td>
                                    <td className="py-3 text-gray-600 dark:text-gray-400 text-xs font-mono">{r.phone_number}</td>
                                    <td className="py-3">
                                        <span className={cn('px-2 py-0.5 text-[11px] font-medium rounded-full', STATUS_COLORS[r.status] || '')}>
                                            {r.status}
                                        </span>
                                    </td>
                                    <td className="py-3 text-gray-500 text-xs">{r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}</td>
                                    <td className="py-3 text-gray-500 text-xs">{r.delivered_at ? new Date(r.delivered_at).toLocaleString() : '—'}</td>
                                    <td className="py-3 text-gray-500 text-xs">{r.read_at ? new Date(r.read_at).toLocaleString() : '—'}</td>
                                    <td className="py-3 text-red-400 text-xs max-w-xs truncate">{r.failure_reason || '—'}</td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="py-8 text-center text-sm text-gray-400">
                                        {campaign?.last_error
                                            ? 'No recipient rows were built for this run \u2014 see the reason above.'
                                            : 'No recipients found.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </ContainerCard>
        </div>
    );
}
